import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { browserTimeZone } from '../lib/timezone';
import {
    bulkCompleteTasks, bulkDeleteTasks, completeTask, createTask, deleteTask,
    reassignTask, reopenTask, updateTask,
} from '../lib/tasksApi';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function useTasks(filters = {}) {
    const { getAuthHeader } = useAuth();
    const params = new URLSearchParams();
    ['status', 'priority', 'contact_id', 'owner_id', 'sort_by', 'sort_dir', 'created_by'].forEach(key => {
        if (filters[key]) params.set(key, filters[key]);
    });
    // The date groups follow the viewer's profile zone on the server; the browser zone is its hint (#904).
    params.set('tz', browserTimeZone());
    const qs = params.toString();
    return useQuery({
        queryKey: ['tasks', qs],
        queryFn: async () => {
            const data = await apiFetch(`/activities/tasks?${qs}`, getAuthHeader());
            return Array.isArray(data) ? data : (data?.data || []);
        },
        staleTime: 30 * 1000,
        refetchInterval: 30 * 1000,
    });
}

// A failed poll is an error, not an all-zero summary: React Query keeps the last good `data` through it, so the
// sidebar badge holds its count instead of vanishing. Before the first answer `data` is undefined; each consumer
// defaults at its read site (the badge to 0, the Tasks page to no summary line).
export function useTasksSummary() {
    const { getAuthHeader } = useAuth();
    const tz = browserTimeZone();
    return useQuery({
        queryKey: ['tasksSummary', tz],
        queryFn: () => apiFetch(`/activities/tasks/summary?tz=${encodeURIComponent(tz)}`, getAuthHeader()),
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

const TASK_FIELDS = [
    'title', 'description', 'contact_id', 'account_id', 'deal_id', 'event_id',
    'due_date', 'priority', 'outcome', 'source', 'reminder_at', 'owner_id',
];

// Every write goes through lib/tasksApi, which tells the app a task changed; the
// bridge bound in App() is the only thing that invalidates queries (#904).

export function useCreateTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({
        mutationFn: (task) => createTask({
            type: 'task', status: 'planned',
            ...Object.fromEntries(TASK_FIELDS.filter(f => task[f] !== undefined).map(f => [f, task[f]])),
        }, getAuthHeader()),
    });
}

export function useCompleteTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: id => completeTask(id, getAuthHeader()) });
}

export function useReopenTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: id => reopenTask(id, getAuthHeader()) });
}

export function useBulkCompleteTasks() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: ids => bulkCompleteTasks(ids, getAuthHeader()) });
}

export function useBulkDeleteTasks() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: ids => bulkDeleteTasks(ids, getAuthHeader()) });
}

export function useUpdateTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: ({ activityId, updates }) => updateTask(activityId, updates, getAuthHeader()) });
}

export function useReassignTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({
        mutationFn: ({ activityId, owner_id, expected_owner_id }) => reassignTask(activityId, { owner_id, expected_owner_id }, getAuthHeader()),
    });
}

export function useSuggestTasks() {
    const { getAuthHeader } = useAuth();
    return useMutation({
        mutationFn: async ({ context_type, context_id } = {}) => {
            const body = {};
            if (context_type) body.context_type = context_type;
            if (context_id) body.context_id = context_id;
            const res = await axios.post(`${API}/ai/suggest-tasks`, body, { headers: getAuthHeader() });
            return res.data.suggestions || [];
        },
    });
}

export function useDeleteTask() {
    const { getAuthHeader } = useAuth();
    return useMutation({ mutationFn: id => deleteTask(id, getAuthHeader()) });
}
